import { useMemo, useCallback, useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { buildWasteCategoryOptions } from "./utils/clientUtils";
import { CharacterizationLoader } from "../../../components/loader/CharacterizationLoader";

const WEIGHT_THRESHOLD_TONNES = 50;

const EditClientDataModal = ({
  isOpen,
  onClose,
  onSave,
  isDataLoading = false,
  isLoading,
  clientName,
  clientId,
  clientCode,
  clientAddress,
  clientList,
  weight,
  wasteCategoryName,
  wasteCategoryList,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({
    clientName: "",
    clientId: "",
    clientCode: "",
    clientAddress: "",
    weight: "",
    wasteCategoryName: "",
  });

  useEffect(() => {
    if (!isOpen) return;
    setFormValues({
      clientName: clientName || "",
      clientId: clientId || "",
      clientCode: clientCode || "",
      clientAddress: clientAddress || "",
      weight: weight || "",
      wasteCategoryName: wasteCategoryName || "",
    });
  }, [
    isOpen,
    clientName,
    clientId,
    clientCode,
    clientAddress,
    weight,
    wasteCategoryName,
  ]);

  const handleSelectionChange = useCallback((event) => {
    const selectedValue = JSON.parse(event.target.value);
    setFormValues((prev) => ({
      ...prev,
      clientName: selectedValue?.name || "",
      clientAddress: selectedValue?.address || "",
      clientId: selectedValue?._id || "",
      // Prefill characterization ref from producer unique ID when empty
      clientCode: prev.clientCode?.trim()
        ? prev.clientCode
        : selectedValue?.clientUniqueId || "",
    }));
  }, []);

  const clientOptions = useMemo(
    () =>
      clientList.map((item) => ({
        key: item._id ?? `${item.name}-${item.address}`,
        value: JSON.stringify(item),
        label: `${item.name} (${
          item?.address &&
          item?.address !== " " &&
          ![undefined, "undefined"].includes(item.address)
            ? item.address
            : "-"
        })`,
      })),
    [clientList],
  );
  const selectedClient = useMemo(() => {
    const match = clientList.find((item) => item._id === formValues.clientId);
    return match ? JSON.stringify(match) : "";
  }, [clientList, formValues.clientId]);

  const wasteOptions = useMemo(
    () => buildWasteCategoryOptions(wasteCategoryList),
    [wasteCategoryList],
  );

  const selectedWasteCategory = useMemo(() => {
    if (!formValues.wasteCategoryName) {
      return "";
    }

    let parsedCategory = null;
    try {
      parsedCategory = JSON.parse(formValues.wasteCategoryName);
    } catch {
      return "";
    }
    if (!parsedCategory || !parsedCategory.wasteCategoryName) {
      return "";
    }

    const matchingOption = wasteOptions.find((option) => {
      try {
        const optionValue = JSON.parse(option.value);
        return (
          optionValue.id === parsedCategory.id ||
          optionValue.wasteCategoryName === parsedCategory.wasteCategoryName
        );
      } catch {
        return false;
      }
    });
    return matchingOption ? matchingOption.value : "";
  }, [formValues.wasteCategoryName, wasteOptions]);

  const weightNum = useMemo(() => {
    if (formValues.weight == null || formValues.weight === "") return null;
    const parsed = parseFloat(String(formValues.weight).replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
  }, [formValues.weight]);

  const isWeightOverThreshold =
    weightNum != null && weightNum > WEIGHT_THRESHOLD_TONNES;

  const handleSave = useCallback(() => {
    // Champs obligatoires : Origine du déchet (clientName) et Poids (weight).
    // Référence d'identification de la caractérisation (clientCode) est optionnel.
    if (!formValues.clientName || !formValues.weight) {
      toast.error(t("Veuillez remplir tous les champs obligatoires"), {
        className: "custom-toast",
      });
      return;
    }
    onSave(formValues);
  }, [formValues, onSave, t]);
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      contentClassName="rounded-lg"
      style={{ maxWidth: "600px" }}
      centered
    >
      <ModalHeader className="flex justify-between items-center pb-3 border-0">
        <span className="text-gray-900 font-semibold text-base">
          {t("Edit data")}
        </span>
        <CloseIcon
          style={{
            color: "#6B7280",
            cursor: "pointer",
            fontSize: "20px",
          }}
          onClick={onClose}
        />
      </ModalHeader>
      <ModalBody className="px-6 position-relative">
        {isDataLoading && (
          <div
            className="position-absolute top-0 left-0 right-0 bottom-0 d-flex align-items-center justify-content-center bg-white/80 rounded"
            style={{ zIndex: 10 }}
          >
            <CharacterizationLoader size="lg" />
          </div>
        )}
        <div className="flex flex-col w-full gap-5">
          {}
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-medium mb-1.5">
              {t("Waste origin")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="client"
                className="appearance-none bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] block w-full px-3 py-2 pr-10 cursor-pointer h-10"
                onChange={handleSelectionChange}
                value={selectedClient}
                disabled={isDataLoading}
              >
                <option value="" className="text-gray-400">
                  ---- {t("Please select")} ----
                </option>
                {clientOptions.map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
          {}
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-medium mb-1.5">
              {t("Identification reference of the characterization")}
            </label>
            <input
              id="clientCode"
              type="text"
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] block w-full px-3 py-2 h-10"
              value={formValues.clientCode ?? ""}
              onChange={(event) =>
                setFormValues((prev) => ({
                  ...prev,
                  clientCode: event.target.value,
                }))
              }
              disabled={isDataLoading}
            />
          </div>
          {}
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-medium mb-1.5">
              {t("Weight")} <span className="text-red-500">*</span>
            </label>
            <div
              className={`flex items-stretch border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-offset-0 ${
                isWeightOverThreshold
                  ? "border-red-500 focus-within:ring-red-500 focus-within:border-red-500"
                  : "border-gray-300 focus-within:ring-[#2AB9B8] focus-within:border-[#2AB9B8]"
              }`}
            >
              <input
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    weight: event.target.value,
                  }))
                }
                value={formValues.weight}
                type="text"
                id="weight"
                className={`border-0 text-sm focus:ring-0 focus:outline-none flex-1 px-3 py-2 h-10 ${
                  isWeightOverThreshold
                    ? "bg-red-50 text-red-700 placeholder-red-300"
                    : "bg-white text-gray-900"
                }`}
                required
                disabled={isDataLoading}
              />
              <span
                className={`text-sm px-3 py-2 flex items-center whitespace-nowrap ${
                  isWeightOverThreshold ? "bg-red-50" : "text-gray-600"
                }`}
                style={isWeightOverThreshold ? { color: "#E11D48" } : undefined}
              >
                {t("tons")}
              </span>
            </div>
            {isWeightOverThreshold && (
              <div
                className="mt-1.5 text-sm italic text-center space-y-0.5"
                style={{ color: "#E11D48" }}
              >
                <p className="m-0">{t("Weight over 50 tonnes")}</p>
                <p className="m-0">{t("Weight over 50 tonnes check unit")}</p>
              </div>
            )}
          </div>
          {}
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-medium mb-1.5">
              {t("Waste name")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="waste"
                className="appearance-none bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-[#2AB9B8] focus:border-[#2AB9B8] block w-full px-3 py-2 pr-10 cursor-pointer h-10"
                value={selectedWasteCategory}
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    wasteCategoryName: event.target.value,
                  }));
                }}
                disabled={isDataLoading}
              >
                <option value="" className="text-gray-400">
                  ---- {t("Please select")} ----
                </option>
                {wasteOptions.map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="flex justify-end gap-2 pt-4 pb-4 border-0 px-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || isDataLoading}
          className="px-4 py-2 bg-[#2AB9B8] text-white rounded-full hover:bg-[#009493] disabled:opacity-50 text-sm font-medium"
        >
          {t("Update")}
        </button>
      </ModalFooter>
    </Modal>
  );
};
export default EditClientDataModal;
